<template>
    <div v-if="hasParams" class="px-3 py-2">

        <template v-if="showCalculationFormulaSwitcher">
            <CalculationFormulaSwitch
                :key="extension.name"
                :extensionName="extension.name"
                :formulas="getExtensionCalculationFormulas(extension.name)"
                :formulaKey="params.formulaKey || ''"
                @update="formulaKey => $emit('update', {path: 'formulaKey', value: formulaKey})"/>
            <template v-if="FormExtensions.CALCULATE_DOCUMENT_TAX === extension.name">
                <hr>
                <div class="custom-control custom-checkbox mt-3">
                    <input :value="!!params.viewOnly"
                           :checked="!!params.viewOnly"
                           @change="$emit('update', {path: 'viewOnly', value: !params.viewOnly})"
                           :disabled="!enabled"
                           class="custom-control-input"
                           type="checkbox"
                           :id="`${extension.name}-viewOnly`">
                    <label class="custom-control-label pt-1" :for="`${extension.name}-viewOnly`">
                        View only
                        <span
                            v-tooltip="'Do not override document tax<br/> (Only display the calculated value at the bottom of the lines)'">
                            <i class="fal fa-info-circle"></i>
                        </span>
                    </label>
                </div>
            </template>
        </template>

        <ContactSelectExtensionParamsEditor
            v-else-if="FormExtensions.CONTACT_SELECT === extension.name"
            :params="params"
            @update="data => $emit('update', data)"/>
        <LineItemSelectExtensionParamsEditor
            v-else-if="FormExtensions.MATCH_ITEM === extension.name"
            :params="params"
            @update="data => $emit('update', data)"/>
        <MatchingExtensionParams
            v-else-if="[FormExtensions.PURCHASE_ORDER, FormExtensions.SUPPLIER_RECEIPT].includes(extension.name)"
            :enabled="enabled"
            :params="params"
            :extension="extension"
            @update="data => $emit('update', data)"/>
        <AssigneesAddonParams
            v-else-if="FormExtensions.DOC_ASSIGNEES === extension.name"
            :params="params"
            :enabled="enabled"
            @update="data => $emit('update', data)"/>
        <LocationsAddonParams
            v-else-if="FormExtensions.DOC_LOCATIONS === extension.name"
            :params="params"
            :enabled="enabled"
            @update="data => $emit('update', data)"/>
        <SubsidiariesAddonParams
            v-else-if="FormExtensions.DOC_SUBSIDIARIES === extension.name"
            :params="params"
            :enabled="enabled"
            @update="data => $emit('update', data)"/>

        <div v-if="FormExtensions.CLOSE_MODAL === extension.name" style="width: 400px;">
            <div class="text-bold">When</div>
            <label>Status changed to</label>
            <multiselect
                :value="Array.isArray(params.statusChangedTo) ? dtStatuses.filter(s => params.statusChangedTo.some(key => key === s.key)) : []"
                @input="value => $emit('update', {path: 'statusChangedTo', value: value.map(v => v.key)})"
                :options="dtStatuses.filter(s => s.isEnabled)"
                :disabled="!enabled"
                track-by="key"
                label="name"
                multiple
                placeholder="Select statuses"
                id="status-changed-to"/>
        </div>
    </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {FormExtensions} from '@/types/document-viewer/extension'
import {getLineTotalCalculationFormulas} from '@/types/document-viewer/extension/calculate/lines/total'
import {getLineTaxCalculationFormulas} from '@/types/document-viewer/extension/calculate/lines/tax'
import {getDocTaxCalculationFormulas} from '@/types/document-viewer/extension/calculate/totals/tax'
import {getDocTotalCalculationFormulas} from '@/types/document-viewer/extension/calculate/totals/total'
import CalculationFormulaSwitch from './calculation/CalculationFormulaSwitch'
import ContactSelectExtensionParamsEditor from './contact-select/ContactSelectExtensionParamsEditor'
import MatchingExtensionParams from './matching/MatchingExtensionParams'
import LineItemSelectExtensionParamsEditor from './lines/LineItemSelectExtensionParamsEditor'
import AssigneesAddonParams from '@/views/documents/subtypes/settings/extensions/assignees/AssigneesAddonParams.vue'
import LocationsAddonParams from '@/views/documents/subtypes/settings/extensions/locations/LocationsAddonParams.vue'
import SubsidiariesAddonParams
    from '@/views/documents/subtypes/settings/extensions/subsidiaries/SubsidiariesAddonParams.vue'

export default {
    name: 'FormExtensionParams',
    components: {
        SubsidiariesAddonParams,
        LocationsAddonParams,
        AssigneesAddonParams,
        MatchingExtensionParams,
        ContactSelectExtensionParamsEditor,
        LineItemSelectExtensionParamsEditor,
        CalculationFormulaSwitch
    },
    props: {
        extension: {
            type: Object,
            required: true
        },
        enabled: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            FormExtensions
        }
    },
    computed: {
        ...mapGetters('documents', [
            'dtStatuses'
        ]),

        hasParams() {
            return [
                FormExtensions.PURCHASE_ORDER,
                FormExtensions.SUPPLIER_RECEIPT,
                FormExtensions.CONTACT_SELECT,
                FormExtensions.MATCH_ITEM,
                FormExtensions.CLOSE_MODAL,
                FormExtensions.CALCULATE_LINE_TOTAL,
                FormExtensions.CALCULATE_LINE_TAX,
                FormExtensions.CALCULATE_DOCUMENT_TOTAL,
                FormExtensions.CALCULATE_DOCUMENT_TAX,
                FormExtensions.DOC_ASSIGNEES,
                FormExtensions.DOC_LOCATIONS,
                FormExtensions.DOC_SUBSIDIARIES
            ].includes(this.extension.name)
        },

        showCalculationFormulaSwitcher() {
            return [
                FormExtensions.CALCULATE_LINE_TOTAL,
                FormExtensions.CALCULATE_LINE_TAX,
                FormExtensions.CALCULATE_DOCUMENT_TOTAL,
                FormExtensions.CALCULATE_DOCUMENT_TAX
            ].includes(this.extension.name)
        },

        params() {
            return this.extension.params || {}
        }
    },
    methods: {
        getExtensionCalculationFormulas(extensionName) {
            switch (extensionName) {
            case FormExtensions.CALCULATE_LINE_TOTAL:
                return getLineTotalCalculationFormulas()
            case FormExtensions.CALCULATE_LINE_TAX:
                return getLineTaxCalculationFormulas()
            case FormExtensions.CALCULATE_DOCUMENT_TOTAL:
                return getDocTotalCalculationFormulas()
            case FormExtensions.CALCULATE_DOCUMENT_TAX:
                return getDocTaxCalculationFormulas()
            }
            return []
        }
    }
}
</script>
